import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Search from "../../../components/Search/CommonSearch";
import { fetchMatter } from "../../../service/matter";
import { resolveEntityName } from "../../../utils/matterFields";
import {
  InboxMatterSearchEmpty,
  InboxMatterSearchResult,
  InboxMatterSearchResultMeta,
  InboxMatterSearchResultName,
  InboxMatterSearchResults,
  InboxMatterSearchWrap,
} from "../InboxStyled";

export const MATTERS_SEARCH_PLACEHOLDER =
  "Search Matter/ Client/ Case Manager/ Principal Attorney";

export const matterSearchLabel = (matter) =>
  matter?.display_name || String(matter?.id || "");

const matterSearchMeta = (matter) =>
  [matter?.practice_area, resolveEntityName(matter?.case_type)]
    .filter(Boolean)
    .join(" · ");

/**
 * Inbox type-ahead that reuses the Matters-page search field and the
 * same `/matters/?search=` lookup (client, matter name, case manager,
 * principal attorney). Picking a row filters the inbox to that matter.
 */
const InboxMatterSearch = ({
  selectedMatterId = null,
  onSelect,
  onClear,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const requestSeqRef = useRef(0);
  const skipNextSearchRef = useRef(false);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const handleSearch = useCallback(
    async (term) => {
      const trimmed = (term || "").trim();
      if (!trimmed) {
        skipNextSearchRef.current = false;
        requestSeqRef.current += 1;
        setResults([]);
        setStatus("idle");
        setOpen(false);
        if (selectedMatterId) onClear();
        return;
      }
      if (skipNextSearchRef.current) {
        skipNextSearchRef.current = false;
        return;
      }

      const seq = ++requestSeqRef.current;
      setStatus("loading");
      setOpen(true);
      try {
        const response = await fetchMatter(
          1,
          null,
          null,
          "asc",
          "display_name",
          10,
          trimmed,
          false
        );
        if (seq !== requestSeqRef.current) return;
        setResults(response?.data?.results || []);
        setStatus("ready");
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        console.error("Inbox matter search failed:", err);
        setResults([]);
        setStatus("failed");
      }
    },
    [onClear, selectedMatterId]
  );

  const handleSelect = (matter) => {
    if (!matter?.id) return;
    skipNextSearchRef.current = true;
    onSelect(matter);
    setInputValue(matterSearchLabel(matter));
    setResults([]);
    setStatus("idle");
    setOpen(false);
  };

  const showDropdown =
    open && (status === "loading" || status === "ready" || status === "failed");

  return (
    <InboxMatterSearchWrap ref={wrapRef}>
      <Search
        searchPlaceholder={MATTERS_SEARCH_PLACEHOLDER}
        inputValue={inputValue}
        handleSearchOnChange={setInputValue}
        handleSearch={handleSearch}
        searchbarwidth="100%"
      />
      {showDropdown && (
        <InboxMatterSearchResults role="listbox">
          {status === "loading" && (
            <InboxMatterSearchEmpty role="status">Searching…</InboxMatterSearchEmpty>
          )}
          {status === "failed" && (
            <InboxMatterSearchEmpty role="alert">
              Search failed — try again.
            </InboxMatterSearchEmpty>
          )}
          {status === "ready" && results.length === 0 && (
            <InboxMatterSearchEmpty role="status">
              No matters matched “{inputValue.trim()}”.
            </InboxMatterSearchEmpty>
          )}
          {status === "ready" &&
            results.map((matter) => {
              const meta = matterSearchMeta(matter);
              return (
                <li key={matter.id} role="presentation">
                  <InboxMatterSearchResult
                    type="button"
                    role="option"
                    onClick={() => handleSelect(matter)}
                  >
                    <InboxMatterSearchResultName>
                      {matterSearchLabel(matter)}
                    </InboxMatterSearchResultName>
                    {meta ? (
                      <InboxMatterSearchResultMeta>{meta}</InboxMatterSearchResultMeta>
                    ) : null}
                  </InboxMatterSearchResult>
                </li>
              );
            })}
        </InboxMatterSearchResults>
      )}
    </InboxMatterSearchWrap>
  );
};

InboxMatterSearch.propTypes = {
  selectedMatterId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default InboxMatterSearch;
